import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    InfoText,
    AutoTable,
    WeezHeader,
    Dropdown,
    DropdownItem,
    Button,
    Card,
    Modal,
    FilterSetController,
    Filter,
    StopPropagation,
    SelectButton,
    Icon,
    ToastContext
} from '@weezevent/nacre';
import { useHistory } from 'react-router-dom';
import ServiceConfigModel from '../../models/serviceConfig';
import Style from './serviceList.module.css';
import ExportButton from '../../components/export/exportButton';
import i18n from '../../i18n/i18n';
import { fetcher } from '@weezevent/weezjs-core';
import ExportPeriod from './exportPeriod';
import moment from 'moment-timezone';

const format_curency = (language, currency, price = 0) => {
    return Intl.NumberFormat(language, { style: 'currency', currency: currency }).format(price);
};

function RowAction({ service, organization, onDeleteServices }) {
    const { t } = useTranslation();
    const history = useHistory();
    return (
        <Dropdown closeOnDocumentClick={true}>
            <Dropdown.Item
                onClick={synth => {
                    history.push(`/ticket/O${organization.pk}/services/${service.uuid}`);
                }}
            >
                {t('ticket.common.action.edit')}
            </Dropdown.Item>

            <Dropdown.Item
                onClick={synth => {
                    onDeleteServices([service.uuid]);
                }}
            >
                {t('ticket.common.action.delete')}
            </Dropdown.Item>
        </Dropdown>
    );
}

function SearchComponent({ objects, filters, onFilters, areServicesLoading }) {
    const { t } = useTranslation();

    return (
        <div className={Style['filter-container']}>
            <FilterSetController
                total={objects?.length}
                isLoadingTotal={areServicesLoading}
                labelTotal={t('ticket.services.table.search.total', { count: objects.length })}
                labelReset={t('ticket.common.action.reset')}
                labelFilters={t('ticket.common.action.filter')}
                initialValues={filters}
                onChange={values => onFilters(values)}
                onReset={() => onFilters({})}
                onDelete={key => {
                    let newFilter = { ...filters };
                    delete newFilter[key];
                    onFilters(newFilter);
                }}
                search={{
                    onChange: (synth, value) => {
                        onFilters({ ...filters, search: value });
                    },
                    handleReset: (synth, value) => {
                        let newFilter = { ...filters };
                        delete newFilter['search'];
                        onFilters(newFilter);
                    },
                    displayResetIcon: true,
                    placeholder: t('ticket.common.action.search'),
                    value: filters?.search || ''
                }}
            >
                <Filter
                    name="service_type__in"
                    labelName={t('ticket.services.table.search.serviceType.title')}
                    multiple
                    options={[
                        {
                            text: t('ticket.services.table.search.serviceType.updateForm'),
                            name: t('ticket.services.table.search.serviceType.updateForm'),
                            key: 'service'
                        }
                    ]}
                    index="1"
                />
            </FilterSetController>
        </div>
    );
}

function ServiceTable({ services, organization, onDeleteServices, filters, onFilters, selectedServices, onSelectedServices, areServicesLoading }) {
    const { t } = useTranslation();
    const history = useHistory();

    if (!services) {
        return <div>{t('ticket.services.table.no_services')}</div>;
    }

    return (
        <Card>
            <SearchComponent filters={filters} onFilters={onFilters} objects={services} areServicesLoading={areServicesLoading} />
            <div className={Style['global-pagesize-container']}>
                <SelectButton
                    tooltipTitle={t('ticket.services.table.noActionTooltip')}
                    title={
                        Object.values(selectedServices).length > 0
                            ? t('ticket.services.table.serviceAction', { count: Object.values(selectedServices).length })
                            : t('ticket.common.action.bulk')
                    }
                    objects={Object.values(selectedServices)}
                >
                    <DropdownItem item={t('ticket.common.action.delete')} onClick={() => onDeleteServices(selectedServices)} />
                </SelectButton>
            </div>

            <AutoTable
                pk="uuid"
                useNew={true}
                className={Style['list__table']}
                selectable={services.length > 0}
                onSelectAll={event => {
                    if (event.target.checked) {
                        return onSelectedServices(services.map(s => s.uuid));
                    }
                    onSelectedServices([]);
                }}
                onObjectSelected={(obj, event) => {
                    let newSelected = Array.from(selectedServices);
                    if (event.target.checked) {
                        return onSelectedServices([...newSelected, obj.uuid]);
                    }
                    let selected = [...newSelected];
                    selected = selected.filter(el => el !== obj.uuid);
                    onSelectedServices(selected);
                }}
                selectedObjects={selectedServices}
                onObjectClicked={(obj, event, index) => history.push(`/ticket/O${organization.pk}/services/${obj.uuid}`)}
                columns={[
                    {
                        key: 'service_name',
                        label: t('ticket.services.table.header.service_name'),
                        render: obj => {
                            return <InfoText subtitle={obj.id}>{obj.name}</InfoText>;
                        }
                    },
                    {
                        key: 'service_type',
                        label: t('ticket.services.table.header.service_type'),
                        render: obj => {
                            return <InfoText subtitle={obj.id}>{obj.service.name}</InfoText>;
                        }
                    },
                    {
                        key: 'price',
                        label: t('ticket.services.table.header.price'),
                        render: obj => {
                            return <InfoText subtitle={obj.id}>{format_curency(i18n.language, obj.currency, obj.price / 100)}</InfoText>;
                        }
                    },
                    {
                        key: 'sales',
                        label: t('ticket.services.table.header.sales'),
                        render: obj => {
                            return <InfoText subtitle={obj.id}>{obj.service_sold}</InfoText>;
                        }
                    },
                    {
                        key: 'quota',
                        label: t('ticket.services.table.header.quota'),
                        render: obj => {
                            return <InfoText subtitle={obj.id}>{obj.quota || t('ticket.services.table.unlimited')}</InfoText>;
                        }
                    },
                    {
                        key: 'action',
                        label: t('ticket.services.table.header.action'),
                        render: obj => {
                            return (
                                <StopPropagation>
                                    <RowAction service={obj} organization={organization} onDeleteServices={onDeleteServices} />
                                </StopPropagation>
                            );
                        }
                    }
                ]}
                objects={[...services]}
            />
            {services.length === 0 && (
                <Card.EmptyState title={t('ticket.services.table.empty-title')} subtitle={t('ticket.services.table.empty-subtitle')} icon={<Icon name="cog" />} />
            )}
        </Card>
    );
}

export default function ServicesList({ organization }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [filters, setFilters] = React.useState({});
    const [selectedServices, setSelectedServices] = React.useState([]);
    const [servicesToDelete, setServiceToDelete] = React.useState([]);
    const [showExportModal, setShowExportModal] = React.useState(false);
    const [startDate, setStartDate] = React.useState(null);
    const [endDate, setEndDate] = React.useState(null);

    const [services, areServicesLoading, sync] = ServiceConfigModel.useApiModel(
        {
            organizationId: organization.id,
            query: {
                expand: 'service',
                ...filters
            }
        },
        [filters]
    );

    const mappingHeaders = [
        { header: t('ticket.export.headers.service_name'), target: 'service_name' },
        { header: t('ticket.export.headers.service_date'), target: 'service_date' },
        { header: t('ticket.export.headers.service_time'), target: 'service_hours' },
        { header: t('ticket.export.headers.order_number'), target: 'order_reference' },
        { header: t('ticket.export.headers.event_id'), target: 'event_id' },
        { header: t('ticket.export.headers.event_name'), target: 'event_name' },
        { header: t('ticket.export.headers.buyers_last_name'), target: 'buyer_last_name' },
        { header: t('ticket.export.headers.buyers_first_name'), target: 'buyer_first_name' },
        { header: t('ticket.export.headers.barcode'), target: 'attendee_barcode' },
        { header: t('ticket.export.headers.attendee_last_name'), target: 'attendee_last_name' },
        { header: t('ticket.export.headers.attendee_first_name'), target: 'attendee_first_name' },
        { header: t('ticket.export.headers.ticket_type'), target: 'attendee_rate' },
        { header: t('ticket.export.headers.origin'), target: 'order_origin' },
        { header: t('ticket.export.headers.paid_price'), target: 'service_paid_price_ttc' },
        { header: t('ticket.export.headers.commission'), target: 'service_commission' },
        { header: t('ticket.export.headers.paid_price_with_vat'), target: 'service_price_without_commission' },
        { header: t('ticket.export.headers.paid_price_without_vat'), target: 'service_paid_price_ht' },
        { header: t('ticket.export.headers.vat_rate'), target: 'service_tax_rate' },
        { header: t('ticket.export.headers.vat_amount'), target: 'service_tax_price' },
        { header: t('ticket.export.headers.currency'), target: 'service_currency' },
        { header: t('ticket.export.headers.transaction_reference'), target: 'transaction_reference' }
    ];

    const onDelete = React.useCallback(() => {
        fetcher
            .delete(`/ticket/organizations/${organization.id}/service_config`, { data: servicesToDelete })
            .then(() => {
                toast.success(t('ticket.services.table.action.deleteModal.success'));
                setServiceToDelete([]);
                setSelectedServices([]);
                sync({ allow_cache: false, cache: false });
            })
            .catch(e => {
                toast.error(t('ticket.services.table.action.deleteModal.error'));
            });
    }, [fetcher, sync, toast, organization, servicesToDelete]);

    return (
        <>
            <ExportButton
                withCustomizableModal
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                organization={organization}
                choiceProcess={'services_config'}
                showButton={false}
                onError={() => toast.error(t('ticket.export.download_state_error'))}
                matchingData={mappingHeaders}
                customTitle={t('ticket.services.export-title')}
                customSubtitle={t('ticket.services.export-subtitle')}
                modalContent={<ExportPeriod onStartDate={setStartDate} onEndDate={setEndDate} />}
                selectedPeriod={{ startDate: moment(startDate), endDate: moment(endDate) }}
                datesFormat={{
                    from_date: date => moment(date).format('YYYY-MM-DD'),
                    to_date: date => moment(date).format('YYYY-MM-DD')
                }}
            />
            <WeezHeader
                title={t('ticket.services.pageTitle')}
                dropdownProps={{ renderWhenClosed: true }}
                rightComponent={
                    <Button key={'service'} primary onClick={() => history.push(`/ticket/O${organization.pk}/services/new`)} label={<div>{t('ticket.common.action.add')}</div>} />
                }
                dropdown={[<Dropdown.Item key="export" onClick={() => setShowExportModal(true)} item={t('ticket.export.download_export')} />]}
            >
                {t('ticket.services.subtitle')}
            </WeezHeader>
            <Modal
                open={servicesToDelete.length ? true : false}
                onClose={() => setServiceToDelete([])}
                title={t('ticket.services.table.action.deleteModal.title')}
                submitButton={<Button primary onClick={onDelete} label={t('ticket.common.action.confirm')} />}
                cancelButton={
                    <Button
                        onClick={() => setServiceToDelete([])}
                        //disabled={loading}
                        label={t('ticket.common.action.back')}
                    />
                }
            >
                <div>
                    {t(servicesToDelete.length === 1 ? 'ticket.services.table.action.deleteModal.body' : 'ticket.services.table.action.deleteModal.body-plural')}
                    {/*
                    TODO : put message here when we will have information about in which widget service is used
                    <Message
                        title={t('ticket.services.table.action.deleteModal.warningTitle')}
                        type={"warning"}
                    >{t('ticket.services.table.action.deleteModal.warning')}</Message> */}
                </div>
            </Modal>
            <ServiceTable
                services={services}
                areServicesLoading={areServicesLoading}
                organization={organization}
                onDeleteServices={setServiceToDelete}
                filters={filters}
                onFilters={setFilters}
                selectedServices={selectedServices}
                onSelectedServices={setSelectedServices}
            />
        </>
    );
}
